class ProductConfigurator {
  constructor(modalSelector = '[data-configurator-modal]') {
    this.modal = document.querySelector(modalSelector);
    if (!this.modal) return;

    this.openButton = null;
    this.openButtonWrapper = null;
    this.injectOpenButton();

    this.closeButton = this.modal.querySelector('.configurator-close-btn');
    this.addToCartButton = this.modal.querySelector('.configurator-add__addons');

    this.mainProductTitle = this.modal.getAttribute('data-main-product-title');
    this.mainProductId = this.modal.getAttribute('data-main-product-id');

    // state (a11y nav)
    this.inProductsNav = false;
    this.activeCategoryIndex = 0;
    this.productFocusables = [];
    this.productFocusIndex = 0;

    // bind handlers
    this.handleWindowClick = this.handleWindowClick.bind(this);
    this.handleCloseClick = this.handleCloseClick.bind(this);
    this.handleAddToCartClick = this.handleAddToCartClick.bind(this);
    this.handleOpenClick = this.handleOpenClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);

    this.setTooltips();

    this.updateTotals();

    this.modal.querySelectorAll('input[data-add-on]').forEach((cb) => {
      cb.dataset.origDisabled = cb.disabled ? 'true' : 'false';
    });

    this.enforceMaxProducts();

    // a11y prep
    this.syncAddButtonsA11y(this.modal);
    this.cacheA11yNodes();
    this.initA11yNav();

    this.tutorialActive = false;
    this.tutorialTippy = null;
    this.tutorialSteps = [];
    this.tutorialStepIndex = 0;
    this.tutorialSkipped = false;
    this.tutorialEnded = false;

    this.bindEvents();

    // Prevent infinite loops when we remove items (that triggers another cart change)
    this._isCartCleaning = false;
    this._cartChangeDebounce = null;

    /*
    document.addEventListener('rebuy:cart.change', (event) => {
      this.handleRebuyCartChange(event);
    });
    */
  }

  /* ---------------------------
   * EVENTS
   * --------------------------- */
  bindEvents() {
    if (this.openButton) {
      this.openButton.addEventListener('click', this.handleOpenClick);
    }
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.handleCloseClick);
    }
    if (this.addToCartButton) {
      this.addToCartButton.addEventListener('click', this.handleAddToCartClick);
    }

    window.addEventListener('click', this.handleWindowClick);

    this.modal.querySelectorAll('input[data-add-on], input[id^="labor-charge"], input.quantity__input').forEach((cb) => {
      cb.addEventListener('change', () => {
        this.updateTotals();

        const name = cb.getAttribute('data-add-on-name') || 'Accessory';
        const price = cb.getAttribute('data-price');

        if (cb.checked) {
          this.announce(`${name} added.`);
        } else {
          this.announce(`${name} removed.`);
        }

        // announce new add-ons total
        const addOnsSpan = this.modal.querySelector('.add-ons-total');
        if (addOnsSpan) {
          this.announce(`Added accessories total ${addOnsSpan.textContent}.`);
        }

        if (cb.matches('input[data-add-on], input.quantity__input')) {
          this.enforceMaxProducts(cb);
          this.syncAddButtonsA11y(this.modal);
        }
      });
    });

    this.modal.addEventListener('keydown', this.handleKeydown);
  }

  handleKeydown(e) {
    if (!this.modal.classList.contains('is-open')) return;

    // ESC closes
    if (e.key === 'Escape') {
      e.preventDefault();
      if (this.tutorialActive) {
        this.tutorialEnded = true;
        this.endTutorial();
        return;
      }
      this.closeModal();
      return;
    }

    // TAB logic:
    // - If inProductsNav: Tab returns to current category title.
    // - Otherwise: Tab cycles only among close, category titles, add-to-cart.
    if (e.key === 'Tab') {      
      if (!this.tutorialActive) {
        e.preventDefault();        
        if (this.inProductsNav) {
          this.exitProductsNav();
          return;
        }

        this.handleMainTab(e.shiftKey);
        return;
      }
    }

    const focusedTitleIndex = this.getFocusedTitleIndex();

    // If focus is on ANY category title, that becomes the active category
    if (focusedTitleIndex !== -1 && focusedTitleIndex !== this.activeCategoryIndex) {
      this.activeCategoryIndex = focusedTitleIndex;
      this.scrollToCategory(this.activeCategoryIndex);
    }

    const isOnTitle = focusedTitleIndex !== -1;

    // Left/Right on title -> change category (and keep focus on title)
    if (isOnTitle && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
      e.preventDefault();
      const dir = e.key === 'ArrowRight' ? 1 : -1;
      this.setActiveCategory(this.activeCategoryIndex + dir, true);
      return;
    }

    // Down on title -> enter products nav
    if (isOnTitle && e.key === 'ArrowDown') {
      e.preventDefault();
      this.enterProductsNav();
      return;
    }

    // Up in products nav -> exit to title
    if (this.inProductsNav && e.key === 'ArrowUp') {
      e.preventDefault();
      this.exitProductsNav();
      return;
    }

    // Left/Right in products nav -> move between product focusables
    if (this.inProductsNav && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
      e.preventDefault();
      const dir = e.key === 'ArrowRight' ? 1 : -1;
      this.moveProductFocus(dir);
      return;
    }
  }

  handleOpenClick() {
    this.lastFocusedElement = document.activeElement;
    this.openModal();
  }

  openModal() {
    if (!this.modal) return;

    this.modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.cacheA11yNodes();
        this.resetToFirstCategory();
        this.syncAddButtonsA11y(this.modal);

        // Focus: first category title (rule)
        if (this.categoryTitles && this.categoryTitles[0]) {
          this.activeCategoryIndex = 0;
          this.focusElement(this.categoryTitles[0]);
        } else if (this.closeButton) {
          this.focusElement(this.closeButton);
        }

        // Tutorial (si aplica)
        if (this.shouldShowTutorial()) {
          this.startTutorial();
        }
      });
    });
  }

  handleWindowClick(event) {
    if (event.target === this.modal) this.closeModal();
  }

  handleCloseClick() {
    this.closeModal();
  }

  closeModal() {
    if (!this.modal) return;

    this.inProductsNav = false;

    if (this.tutorialActive) this.endTutorial();
    this.modal.classList.remove('is-open');
    document.body.style.overflow = '';

    requestAnimationFrame(() => {
      if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
        this.lastFocusedElement.focus();
      }
      this.resetToFirstCategory();
    });
  }

  /* ---------------------------
   * TOOLTIP (product descriptions)
   * --------------------------- */
  setTooltips() {
    tippy('.configurator-product-card__link', {
      content(reference) {
        const wrapper = reference
          .closest('.configurator-product-card')
          .querySelector('.product-description-wrapper');

        if (!wrapper) return '';

        const clone = wrapper.cloneNode(true);

        // remove <li> that contains a link
        clone.querySelectorAll('li').forEach((li) => {
          if (li.querySelector('a')) li.remove();
        });

        // replace remaining <a> with its children
        clone.querySelectorAll('a').forEach((a) => {
          a.replaceWith(...a.childNodes);
        });

        return clone.innerHTML;
      },
      arrow: true,
      placement: 'auto',
      hideOnClick: false,
      allowHTML: true,
    });
  }

  /* ---------------------------
   * BUTTON INJECTION
   * --------------------------- */
  injectOpenButton() {
    this.openButtonWrapper = document.querySelector('.open-configurator-btn-wrapper');
    this.openButton = this.openButtonWrapper
      ? this.openButtonWrapper.querySelector('.open-configurator-btn')
      : null;

    const target = document.querySelector('product-form .product-form__buttons');
    if (target && this.openButtonWrapper) {
      target.appendChild(this.openButtonWrapper);
    }
  }

  /* ---------------------------
   * ADD TO CART
   * --------------------------- */
  handleAddToCartClick() {
    const selectedAddOns = [];
    const laborMap = {}; // { [laborVariantId]: { title, addOnVariantId } }                         

    selectedAddOns.push(this.mainProductId);

    const addOnCheckboxes = this.modal.querySelectorAll('input[data-add-on]:checked');

    addOnCheckboxes.forEach((checkbox) => {
      const addOnVariantId = checkbox.value;
      selectedAddOns.push(checkbox.value);

      const addOnTitle = checkbox.getAttribute('data-add-on-name') || this.mainProductTitle;

      const laborChargeCheckbox = this.modal.querySelector(
        'input[id="labor-charge-' + checkbox.value + '"]'
      );

      if (laborChargeCheckbox && laborChargeCheckbox.checked) {
        const laborVariantId = laborChargeCheckbox.value;
        selectedAddOns.push(laborVariantId);
        laborMap[laborVariantId] = {
          title: addOnTitle,
          addOnVariantId: addOnVariantId
        };
      }
    });

    this.addMultipleProductsToCart(selectedAddOns, laborMap);
  }

  addMultipleProductsToCart(variantIds, laborMap = {}) {
    // Build the payload for /cart/add(.js)
    const items = variantIds.map((variantId) => {
      // Main product always goes with quantity 1 and no special properties
      if (variantId == this.mainProductId) {
        return { id: variantId, quantity: 1 };
      }

      // If this variantId is a "labor charge" variant, use the add-on's quantity
      const laborInfo = laborMap[variantId];
      if (laborInfo) {
        // Labor quantity must match the quantity selected for its associated add-on
        const quantityInput = this.modal.querySelector(
          `input[data-quantity-variant-id="${CSS.escape(laborInfo.addOnVariantId)}"]`
        );

        let quantity = 1;
        if (quantityInput) {
          const parsed = parseInt(quantityInput.value, 10);
          quantity = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
        }

        return {
          id: variantId,
          quantity,
          properties: {
            _configurator: 'true',
            _configurator_product_type: 'labor-charge',
            _addOnVariantId: laborInfo.addOnVariantId,
            For: laborInfo.title,
          },
        };
      }

      // Regular add-on: use its own quantity input (defaults to 1)
      const quantityInput = this.modal.querySelector(
        `input[data-quantity-variant-id="${CSS.escape(variantId)}"]`
      );

      let quantity = 1;
      if (quantityInput) {
        const parsed = parseInt(quantityInput.value, 10);
        quantity = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
      }

      return {
        id: variantId,
        quantity,
        properties: {
          _configurator: 'true',
          _configurator_product_type: 'add-on',
          _addOnVariantId: variantId,
          'Add-On For': this.mainProductTitle,
        },
      };
    });

    // Ensure we hit the JSON endpoint (some themes return /cart/add without .js)
    const url = routes.cart_add_url.endsWith('.js')
      ? routes.cart_add_url
      : `${routes.cart_add_url}.js`;

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items }),
    })
      .then(async (res) => {
        // Attempt to parse JSON no matter what status we get
        const data = await res.json().catch(() => ({}));

        // fetch() does NOT reject on HTTP errors (e.g. 422). We must handle that here.
        if (!res.ok) {
          throw data; // Usually includes { status: 422, description: '...' }
        }

        // Shopify sometimes returns an error object with a "status" field
        if (data && data.status) {
          throw data;
        }

        return data;
      })
      .then((response) => {
        // Notify any theme listeners that the cart changed
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'configurator',
            productVariantId: null,
            cartData: response,
          });
        }

        // Update cart drawer/notification UI if present
        const cartDrawer =
          document.querySelector('cart-drawer') ||
          document.querySelector('cart-notification') ||
          null;

        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(response);
        }

        // Close configurator modal on success
        this.closeModal();
        this.clearConfigurator();
      })
      .catch((err) => {
        // Show a generic message to the user (we don't need item-specific errors)
        const message = 'One of the selected products does not have sufficient stock.';

        // Always log the real error for debugging
        console.error('Cart add error:', err);

        // Notify any theme listeners about the error
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'configurator',
            productVariantId: null,
            errors: err?.errors || err?.description,
            message,
          });
        }

        // Keep the modal open and display the error message inside it
        this.showConfiguratorError(message);
      });
  }

  showConfiguratorError(message) {
    const el =
      this.modal.querySelector('[data-configurator-error]') ||
      this.modal.querySelector('.configurator-error');

    if (el) {
      el.textContent = message;
      el.hidden = false;
      return;
    }

    // fallback: insertar arriba del modal content
    const div = document.createElement('div');
    div.className = 'configurator-error';
    div.setAttribute('role', 'alert');
    div.textContent = message;

    const target = this.modal.querySelector('.modal__content') || this.modal;
    target.prepend(div);
  }

  /* ---------------------------
   * CART CHANGE
   * --------------------------- */
  handleRebuyCartChange(event) {
    // Debounce because cart updates can fire in bursts
    clearTimeout(this._cartChangeDebounce);

    this._cartChangeDebounce = setTimeout(async () => {
      if (this._isCartCleaning) return;

      try {
        this._isCartCleaning = true;

        const cart = await this.getCartFromEventOrFetch(event);
        await this.cleanupOrphanLaborCharges(cart);
      } catch (e) {
        console.error('[Configurator] Error cleaning orphan labor charges:', e);
      } finally {
        this._isCartCleaning = false;
      }
    }, 100);
  }

  async getCartFromEventOrFetch(event) {
    // Prefer cart from the event if present
    const cartFromEvent = event?.detail?.cart;
    if (cartFromEvent && Array.isArray(cartFromEvent.items)) return cartFromEvent;

    // Fallback to fetching the current cart state
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  isConfiguratorItem(item) {
    return item?.properties?._configurator === 'true';
  }

  getItemProperty(item, key) {
    return item?.properties?.[key];
  }

  async cleanupOrphanLaborCharges(cart) {
    const items = cart?.items || [];
    if (!items.length) return;

    // Build a set of add-on variant IDs currently present in cart
    const addOnVariantIdsInCart = new Set(
      items
        .filter((item) => this.isConfiguratorItem(item))
        .filter((item) => this.getItemProperty(item, '_configurator_product_type') === 'add-on')
        .map((item) => String(item.variant_id))
    );

    // Find labor-charge line items whose linked add-on is missing
    const orphanLaborItems = items.filter((item) => {
      if (!this.isConfiguratorItem(item)) return false;
      if (this.getItemProperty(item, '_configurator_product_type') !== 'labor-charge') return false;

      const linkedAddOnVariantId = String(this.getItemProperty(item, '_addOnVariantId') || '');
      if (!linkedAddOnVariantId) return false;

      return !addOnVariantIdsInCart.has(linkedAddOnVariantId);
    });

    if (!orphanLaborItems.length) return;

    // Remove each orphan labor-charge line item.
    // Use item.key to remove the exact line item (safer than variant_id)
    for (const laborItem of orphanLaborItems) {
      await this.removeLineItemByKey(laborItem.key);
    }
  }

  async removeLineItemByKey(lineItemKey) {
    const url = routes.cart_change_url.endsWith('.js')
      ? routes.cart_change_url
      : `${routes.cart_change_url}.js`;

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: lineItemKey, quantity: 0 }),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Failed to remove line item key=${lineItemKey}. HTTP ${res.status}. ${text}`);
    }

    return res.json();
  }

  /* ---------------------------
   * CLEAR CONFIGURATOR
   * --------------------------- */
  clearConfigurator(){
    //, 
    this.modal.querySelectorAll('input[data-add-on], input[id^="labor-charge"]').forEach((cb) => {
      cb.checked = false;
    })
    this.modal.querySelectorAll('input.quantity__input').forEach((qi) => {
      qi.value = 1;
    })
  }

  /* ---------------------------
   * TOTALS
   * --------------------------- */
  updateTotals() {
    let addOnsTotal = 0;

    const checkedAddOns = this.modal.querySelectorAll('input[data-add-on]:checked');
    checkedAddOns.forEach((checkbox) => {
      const price = parseFloat(checkbox.getAttribute('data-price') || 0);
      const quantityInput = this.modal.querySelector(`input[data-quantity-variant-id="${CSS.escape(checkbox.value)}"]`);
      addOnsTotal += price * (quantityInput ? parseInt(quantityInput.value, 10) : 1);

      const laborChargeCheckbox = this.modal.querySelector(`input[id="labor-charge-${checkbox.value}"]`);
      if (laborChargeCheckbox && laborChargeCheckbox.checked) {
        const laborPrice = parseFloat(laborChargeCheckbox.getAttribute('data-price') || 0);
        addOnsTotal += laborPrice  * (quantityInput ? parseInt(quantityInput.value, 10) : 1);
      }
    }); 

    const addOnsSpan = this.modal.querySelector('.add-ons-total');
    if (addOnsSpan) addOnsSpan.textContent = this.formatMoney(addOnsTotal, '${{amount}}');

    const mainPrice = parseFloat(this.modal.getAttribute('data-main-product-price') || 0);

    const totalSpan = this.modal.querySelector('.total');
    if (totalSpan) {
      const total = mainPrice + addOnsTotal;
      totalSpan.textContent = this.formatMoney(total, '${{amount}}');
    }
  }

  /* ---------------------------
   * MAX PRODUCTS PER CATEGORY
   * --------------------------- */
  enforceMaxProducts(changedCheckbox = null) {
    const categories = changedCheckbox
      ? [changedCheckbox.closest('.configurator-category[data-max-products]')].filter(Boolean)
      : Array.from(this.modal.querySelectorAll('.configurator-category[data-max-products]'));

    categories.forEach((cat) => {
      const max = parseInt(cat.getAttribute('data-max-products'), 10);
      if (!Number.isFinite(max) || max <= 0) return;
/*
      const addOnCheckboxes = Array.from(cat.querySelectorAll('input[data-add-on]'));
      const checkedCount = addOnCheckboxes.filter((cb) => cb.checked).length;
      const reachedMax = checkedCount >= max;
*/

const addOnCheckboxes = Array.from(cat.querySelectorAll('input[data-add-on]'));

const selectedQty = addOnCheckboxes.reduce((sum, cb) => {
  if (!cb.checked) return sum;

  const variantId = cb.value;
  const quantityInput = this.modal.querySelector(
    `input[data-quantity-variant-id="${CSS.escape(variantId)}"]`
  );

  const qty = quantityInput ? parseInt(quantityInput.value, 10) : 1;
  return sum + (Number.isFinite(qty) && qty > 0 ? qty : 1);
}, 0);

const reachedMax = selectedQty >= max;


      addOnCheckboxes.forEach((cb) => {
        const origDisabled = cb.dataset.origDisabled === 'true';
        if (origDisabled) return;

        cb.disabled = reachedMax && !cb.checked;

        const label = cb.nextElementSibling;
        if (!label) return;

        if (cb.disabled) {
          if (!label._tippy) {
            tippy(label, {
              content: 'You have reached the maximum number of selectable products in this category.',
              placement: 'top',
              arrow: true,
              trigger: 'mouseenter focus',
            });
          }
          label.setAttribute('aria-disabled', 'true');
          label.setAttribute('aria-label', `Maximum of ${max} products can be selected in this category.`);
        } else if (label._tippy) {
          label._tippy.destroy();
        }
      });
    });
  }

  /* ---------------------------
   * RESET SLIDERS
   * --------------------------- */
  resetToFirstCategory() {
    const mainSliderComponent = this.modal.querySelector('.configurator-categories-slider');
    if (!mainSliderComponent) return;

    const mainSlider = mainSliderComponent.querySelector('.slider');
    if (!mainSlider) return;

    mainSlider.scrollTo({ left: 0, behavior: 'auto' });
    mainSlider.dispatchEvent(new Event('scroll', { bubbles: true }));

    const items = mainSliderComponent.sliderItemsToShow || mainSliderComponent.sliderItems;
    if (typeof mainSliderComponent.update === 'function' && items && items.length) {
      mainSliderComponent.currentPage = 1;
      mainSliderComponent.update();
    }

    const firstCategorySlide =
      mainSlider.querySelector('.slider__slide') || mainSlider.querySelector('[id^="Slide-"]');

    if (!firstCategorySlide) return;

    firstCategorySlide.querySelectorAll('slider-component.configurator-products-carousel').forEach((comp) => {
      const inner = comp.querySelector('.slider');
      if (!inner) return;

      inner.scrollTo({ left: 0, behavior: 'auto' });
      inner.dispatchEvent(new Event('scroll', { bubbles: true }));

      const innerItems = comp.sliderItemsToShow || comp.sliderItems;
      if (typeof comp.update === 'function' && innerItems && innerItems.length) {
        comp.currentPage = 1;
        comp.update();
      }
    });

    // refresh a11y caches too
    this.cacheA11yNodes();
    this.activeCategoryIndex = 0;
    this.inProductsNav = false;
  }

  /* ---------------------------
   * A11Y: SETUP + NAV RULES
   * --------------------------- */
  cacheA11yNodes() {
    this.mainSliderComponent = this.modal.querySelector('.configurator-categories-slider');
    this.mainSlider = this.modal.querySelector('.configurator-categories-slider .slider');

    // category slides
    this.categorySlides = this.mainSlider
      ? Array.from(this.mainSlider.querySelectorAll('.configurator-category'))
      : [];

    // category titles (focus targets for TAB + arrows)
    this.categoryTitles = this.categorySlides
      .map((slide) => slide.querySelector('.configurator-category__title'))
      .filter(Boolean);

    // make titles focusable
    this.categoryTitles.forEach((h2) => {
      h2.setAttribute('tabindex', '0');
      // optional: give it a role (it behaves like a “tab”)
      h2.setAttribute('role', 'tab');
    });
  }

  initA11yNav() {
    // only once
    if (this._a11yInit) return;
    this._a11yInit = true;
  }

  getFocusedTitleIndex() {
    if (!this.categoryTitles || !this.categoryTitles.length) return -1;
    return this.categoryTitles.indexOf(document.activeElement);
  }

  handleMainTab(shift) {
    const focusables = this.getMainTabStops();
    if (!focusables.length) return;

    const active = document.activeElement;
    let idx = focusables.indexOf(active);

    if (idx === -1) idx = 0;
    idx = shift ? idx - 1 : idx + 1;

    if (idx < 0) idx = focusables.length - 1;
    if (idx >= focusables.length) idx = 0;

    const nextEl = focusables[idx];
    this.focusElement(nextEl);

    // IMPORTANT: if we landed on a title via TAB, sync activeCategoryIndex + align slider
    const titleIdx = this.categoryTitles ? this.categoryTitles.indexOf(nextEl) : -1;
    if (titleIdx !== -1) {
      this.activeCategoryIndex = titleIdx;
      this.scrollToCategory(titleIdx);
    }

    // leaving products nav (just in case)
    this.inProductsNav = false;
  }

  getMainTabStops() {
    const stops = [];
    if (this.closeButton) stops.push(this.closeButton);
    if (this.categoryTitles && this.categoryTitles.length) stops.push(...this.categoryTitles);
    if (this.addToCartButton) stops.push(this.addToCartButton);
    return stops.filter(Boolean);
  }

  setActiveCategory(index, focusTitle = true) {
    const count = this.categorySlides.length;
    if (!count) return;

    let next = index;
    if (next < 0) next = count - 1;
    if (next >= count) next = 0;

    this.activeCategoryIndex = next;
    this.scrollToCategory(next);

    if (focusTitle && this.categoryTitles[next]) {
      this.focusElement(this.categoryTitles[next]);
    }

    // leaving products nav when switching categories from title
    this.inProductsNav = false;
  }

  scrollToCategory(index) {
    if (!this.mainSlider || !this.categorySlides || !this.categorySlides.length) return;

    const count = this.categorySlides.length;
    let i = index;
    if (i < 0) i = count - 1;
    if (i >= count) i = 0;

    const slide = this.categorySlides[i];
    if (!slide) return;

    this.mainSlider.scrollTo({ left: slide.offsetLeft, behavior: 'auto' });
    this.mainSlider.dispatchEvent(new Event('scroll', { bubbles: true }));
  }

  enterProductsNav() {
    this.buildProductFocusables();

    if (!this.productFocusables.length) return;

    this.inProductsNav = true;
    this.productFocusIndex = 0;

    this.focusElement(this.productFocusables[this.productFocusIndex]);
  }

  exitProductsNav() {
    this.inProductsNav = false;

    const title = this.categoryTitles && this.categoryTitles[this.activeCategoryIndex];
    if (title) this.focusElement(title);
  }

  buildProductFocusables() {
    this.productFocusables = [];
    this.productFocusIndex = 0;

    const slide = this.categorySlides[this.activeCategoryIndex];
    if (!slide) return;

    // order per card: product link, Add label
    slide.querySelectorAll('.configurator-product-card').forEach((card) => {
      
      const link = card.querySelector('.configurator-product-card__link');
      const quantityInput = card.querySelector('input.quantity__input');
      const laborInput = card.querySelector('input[data-type="labor-charge"]');
      const label = card.querySelector('.configurator-product-card__cta input[data-add-on] + label');

      if (link) this.productFocusables.push(link);
      if (laborInput) this.productFocusables.push(laborInput);
      if (quantityInput) this.productFocusables.push(quantityInput);
      if (label && label.tabIndex !== -1) this.productFocusables.push(label);
      
    });

    // filter out hidden/disabled-ish
    this.productFocusables = this.productFocusables.filter((el) => this.isActuallyFocusable(el));
  }

  moveProductFocus(dir) {
    if (!this.productFocusables.length) return;

    let next = this.productFocusIndex + dir;
    if (next < 0) next = this.productFocusables.length - 1;
    if (next >= this.productFocusables.length) next = 0;

    this.productFocusIndex = next;
    this.focusElement(this.productFocusables[this.productFocusIndex]);
  }

  syncAddButtonsA11y(scope = this.modal) {
    if (!scope) return;

    const inputs = scope.querySelectorAll(
      '.configurator-product-card__cta input[type="checkbox"][data-add-on]'
    );

    inputs.forEach((input) => {
      const id = input.id;
      if (!id) return;

      const label = scope.querySelector(`label[for="${CSS.escape(id)}"]`);
      if (!label) return;

      // label focusability based on input state
      label.tabIndex = input.disabled ? -1 : 0;
      label.setAttribute('role', 'button');
      label.setAttribute('aria-disabled', input.disabled ? 'true' : 'false');

      // keyboard toggle
      if (!label._pcKeyHandlerBound) {
        label.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          if (input.disabled) return;

          input.checked = !input.checked;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
        label._pcKeyHandlerBound = true;
      }
    });
  }

  focusElement(el) {
    if (!el) return;

    const canFocus =
      el.tabIndex >= 0 ||
      /^(A|BUTTON|INPUT|SELECT|TEXTAREA)$/.test(el.tagName) ||
      el.hasAttribute('contenteditable');

    if (!canFocus) el.setAttribute('tabindex', '-1');

    el.focus({ preventScroll: true });
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  isActuallyFocusable(el) {
    if (!el) return false;

    // disabled attribute
    if (el.hasAttribute('disabled')) return false;

    // aria-hidden
    if (el.getAttribute('aria-hidden') === 'true') return false;

    const cs = window.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;

    // if totally collapsed, treat as not focusable for UX
    if (el.offsetWidth === 0 && el.offsetHeight === 0) return false;

    return true;
  }

  announce(message) {
    const region = this.modal.querySelector('#configurator-live-region');
    if (!region) return;

    // clear and set via rAF to ensure screen readers pick it up
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  /* ---------------------------
   * MONEY FORMAT
   * --------------------------- */
  formatMoney(cents, format = '{{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');

    const placeholderRegex = /{{\s*(\w+)\s*}}/;
    const formatString = format;
    let value = '';

    function defaultOption(opt, def) {
      return typeof opt === 'undefined' ? def : opt;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');

      if (isNaN(number) || number == null) return 0;

      number = (number / 100).toFixed(precision);

      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      const centsPart = parts[1] ? decimal + parts[1] : '';

      return dollars + centsPart;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  /* ---------------------------
   * TUTORIAL
   * --------------------------- */
  shouldShowTutorial() {
    try {
      return localStorage.getItem('pc_configurator_tutorial_skipped') !== '1';
    } catch (e) {
      return true;
    }
  }

  startTutorial() {

    this.tutorialEnded = false;
    // avoid starting if already active
    if (this.tutorialActive) return;

    // targets: 1) first add button of current category
    const slide = this.categorySlides && this.categorySlides[this.activeCategoryIndex];
    const firstAddLabel = slide
      ? slide.querySelector('.configurator-product-card__cta input[data-add-on] + label')
      : null;

    // 2) Title of active category
    const activeTitle = this.categoryTitles && this.categoryTitles[this.activeCategoryIndex]
      ? this.categoryTitles[this.activeCategoryIndex]
      : null;

    // 3) Final add all button
    const addAllBtn = this.addToCartButton;

    // if some targets missing, abort
    if (!firstAddLabel || !activeTitle || !addAllBtn) return;

    this.tutorialActive = true;
    this.tutorialSkipped = false;
    this.modal.classList.add('pc-tutorial-active');

    this.tutorialSteps = [
      {
        el: firstAddLabel,
        content: 'Use this button to add an accessory to the main product.'
      },
      {
        el: activeTitle,
        content: 'Use Left/Right to switch categories, and Down to browse accessories.'
      },
      {
        el: addAllBtn,
        content: 'When you are ready, add everything to your cart here.'
      }
    ];

    this.tutorialStepIndex = 0;
    this.showTutorialStep(0);
  }

  showTutorialStep(index) {
    const step = this.tutorialSteps[index];
    if (!step || !step.el) {
      this.endTutorial();
      return;
    }

    // destroy previous tippy if any
    if (this.tutorialTippy) {
      this.tutorialTippy.destroy();
      this.tutorialTippy = null;
    }

    const html = `
      <div class="pc-tutorial">
        <div class="pc-tutorial__text">${step.content}</div>
        <div class="pc-tutorial__actions">
          <button type="button" data-tut-skip>Skip</button>
          <button type="button" data-tut-next>${index === this.tutorialSteps.length - 1 ? 'Done' : 'Next'}</button>
        </div>
      </div>
    `;

    this.tutorialTippy = tippy(step.el, {
      content: html,
      allowHTML: true,
      interactive: true,
      trigger: 'manual',
      placement: 'top',
      arrow: true,
      theme: 'tutorial',
      hideOnClick: false,
      onShown: (instance) => {
        // focus the step element
        this.focusElement(instance.popper.querySelector('button[data-tut-skip]'));

        const box = instance.popper;
        const nextBtn = box.querySelector('[data-tut-next]');
        const skipBtn = box.querySelector('[data-tut-skip]');

        if (nextBtn) {
          nextBtn.addEventListener('click', () => instance.hide(), { once: true });
        }

        if (skipBtn) {
          skipBtn.addEventListener('click', () => {
            this.tutorialSkipped = true;
            try { localStorage.setItem('pc_configurator_tutorial_skipped', '1'); } catch (e) {}
            instance.hide(); // will trigger onHidden
          }, { once: true });
        }
      },
      onHidden: () => {
        if(this.tutorialEnded) {         
          return;
        }

        if (this.tutorialSkipped) {
          this.endTutorial();
          return;
        }

        const nextIndex = index + 1;
        if (nextIndex >= this.tutorialSteps.length) {
          this.endTutorial();
        } else {
          this.tutorialStepIndex = nextIndex;
          this.showTutorialStep(nextIndex);
        }
      }
    });

    // tippy() can return an array
    if (Array.isArray(this.tutorialTippy)) this.tutorialTippy = this.tutorialTippy[0];

    this.tutorialTippy.show();
  }

  endTutorial() {
    if (this.tutorialTippy) {
      this.tutorialTippy.destroy();
      this.tutorialTippy = null;
    }
    
    this.tutorialActive = false;
    this.tutorialSteps = [];
    this.tutorialStepIndex = 0;
    this.tutorialSkipped = false;
    this.tutorialEnded = true;

    this.modal.classList.remove('pc-tutorial-active');

    // return focus to active category title
    const title = this.categoryTitles && this.categoryTitles[this.activeCategoryIndex];
    if (title) this.focusElement(title);
  }
}

document.addEventListener('DOMContentLoaded', function () {
  new ProductConfigurator('[data-configurator-modal]');
});